import React, { useContext, useMemo } from "react";
import { useCallback, useEffect } from "react";
import { useHistory } from "react-router";
import RestAPIClient from "services/RestAPIClient";
import useSWR, { cache, SWRConfiguration } from "swr";
import { AuthContext } from "./components/AuthProvider/context";
import {
  IFRAME_HEIGHT,
  IFRAME_LEFT,
  IFRAME_TOP,
  IFRAME_WIDTH,
  SSOAction,
  SSO_AUTH_URL,
  SSO_ORIGIN,
  USER_TOKEN_KEY,
} from "./constants";
import authService from "./services/authService";
import {
  ISSOMessageRequest,
  ISSOMessageResponse,
  ISSOUser,
} from "./services/typings";
import { useDestroyChat } from "components/chat/hooks";

const publicPath = ["/login", "/register"];

const useAuth = () => {
  const auth = useMemo(() => authService.checkTokenValid(), []);

  const { logout } = useLogout(true);

  useEffect(() => {
    if (!auth) {
      logout();
    }
  }, [logout, auth]);

  return {
    auth,
  };
};

const useLogout = (redirect: boolean = false) => {
  const { replace } = useHistory();
  const { destroy } = useDestroyChat();
  const logout = useCallback(() => {
    RestAPIClient.token = undefined;
    authService.removeToken();
    cache.clear();

    destroy();

    !publicPath.includes(window.location.pathname) &&
      replace(
        redirect
          ? `/login?${
              window.location.pathname !== "/"
                ? "redirect=" + encodeURIComponent(window.location.pathname)
                : ""
            }`
          : "/login"
      );
  }, [destroy, replace, redirect]);
  return {
    logout,
  };
};

export const useSSO = () => {
  const ssoWindowRef = React.useRef<Window | null>(null);
  const { ssoHiddenIframeRef, status } = useContext(AuthContext);

  const sendMsgEvent = React.useCallback(
    (requestMsg: ISSOMessageRequest, options?: { windowMode?: boolean }) =>
      new Promise<any>((resolve, reject) => {
        const { windowMode } = options || {};
        const messageChannel = new MessageChannel();
        const transferer = windowMode ? window : messageChannel.port1;
        transferer.onmessage = (event: MessageEvent<ISSOMessageResponse>) => {
          if (!event.origin || event.origin === SSO_ORIGIN) {
            const { action, status, payload, error } = event.data;
            if (action === requestMsg.action) {
              if (status === "SUCCESS") {
                if (windowMode && ssoWindowRef.current) {
                  ssoWindowRef.current?.close();
                }
                resolve(payload);
              } else {
                reject(error);
              }
            }
          }
        };
        if (windowMode) {
          const features = `width=${IFRAME_WIDTH}, height=${IFRAME_HEIGHT}, top=${IFRAME_TOP}, left=${IFRAME_LEFT}`;
          ssoWindowRef.current = window.open(SSO_AUTH_URL, undefined, features);
        } else {
          ssoHiddenIframeRef.current?.contentWindow?.postMessage(
            requestMsg,
            SSO_AUTH_URL,
            [messageChannel.port2]
          );
        }
      }),
    [ssoHiddenIframeRef]
  );

  const login = React.useCallback(
    () =>
      sendMsgEvent(
        {
          action: SSOAction.LOGIN,
        },
        { windowMode: true }
      ),
    [sendMsgEvent]
  );

  const logout = React.useCallback(
    (accountIndex: number) =>
      sendMsgEvent({
        action: SSOAction.LOGOUT,
        payload: accountIndex,
      }),
    [sendMsgEvent]
  );

  const getAccessToken = React.useCallback(
    (accountIndex: number) =>
      sendMsgEvent({
        action: SSOAction.GET_ACCESS_TOKEN,
        payload: accountIndex,
      }),
    [sendMsgEvent]
  );

  const getAccountList = React.useCallback(
    () =>
      sendMsgEvent({
        action: SSOAction.GET_ACCOUNT_LIST,
      }),
    [sendMsgEvent]
  );

  return {
    ssoHiddenIframeRef,
    login,
    logout,
    getAccessToken,
    getAccountList,
    status,
  };
};

export const useAuthUsers = (config: SWRConfiguration = {}) => {
  const { getAccountList, status } = useSSO();
  const { data, error } = useSWR<ISSOUser[]>(
    "AUTH_USERS",
    async () => {
      try {
        const res = await getAccountList();
        return res?.accounts || [];
      } catch (e) {
        return [];
      }
    },
    {
      refreshInterval: 0,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      ...config,
    }
  );
  const loading = data === undefined && !error;
  return {
    data,
    loading,
    error,
  };
};

const useToken = (config: SWRConfiguration = {}) => {
  const { data } = useSWR(USER_TOKEN_KEY, authService.getToken, {
    refreshInterval: 0,
  });
  return data;
};

export { useAuth, useLogout, useToken };
